import { supabase } from "src/lib/supabaseClient";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import {
  CreateRecurringSessionsInput,
  SessionData,
  SessionWriteInput,
  sessionsService,
} from "src/services/sessionsService";
import {
  SchedulingParticipant,
  SchedulingParticipantsResponse,
} from "src/types/scheduling";

const RECENT_LIMIT = 3;

export interface SchedulingPurchasedPlan {
  id: string;
  name: string;
  student_id: string;
  product_name: string;
  product_type: string;
  subjectName?: string;
  default_tutor_id?: string | null;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
  total_sessions?: number | null;
  remaining_sessions?: number | null;
  subscription_id?: string | null;
  studentName: string;
  tutorName?: string;
}

export interface SchedulingBatchCurriculumUnit {
  id: string;
  unit_id: string;
  name: string;
  description?: string | null;
  custom_tutor_id?: string | null;
  subjectName?: string;
}

export interface CreateAdminRecurringSessionsInput
  extends Omit<CreateRecurringSessionsInput, "occurrences" | "recurring_pattern" | "scheduling_type"> {
  batch_id: string;
  weekdays: number[];
  start_date: string;
  timezone: string;
}

const formatName = (firstName?: string | null, lastName?: string | null, fallback = "Unnamed") => {
  const name = [firstName, lastName].filter(Boolean).join(" ").trim();
  return name || fallback;
};

const joinKeywords = (values: Array<string | number | null | undefined | string[]>) =>
  values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value) => value !== null && value !== undefined && `${value}`.trim().length > 0)
    .join(" ");

const markRecent = <T extends SchedulingParticipant>(participants: T[]) =>
  participants.map((participant, index) => ({
    ...participant,
    recent: index < RECENT_LIMIT,
  }));

const fetchStudentsForScheduling = async (): Promise<SchedulingParticipant[]> => {
  const { data: profilesData, error: profilesError } = await supabase
    .from("profiles")
    .select("id, first_name, last_name, email, timezone, profile_picture_url, created_at")
    .eq("user_type", "student")
    .order("created_at", { ascending: false });

  if (profilesError) throw profilesError;
  if (!profilesData?.length) return [];

  const studentIds = profilesData.map((profile) => profile.id);
  const { data: studentsData, error: studentsError } = await supabase
    .from("students")
    .select("id, education_level, subjects_of_interest, location")
    .in("id", studentIds);

  if (studentsError) throw studentsError;

  const studentsById = new Map((studentsData || []).map((student) => [student.id, student]));

  return markRecent(
    profilesData.map((profile) => {
      const student = studentsById.get(profile.id);
      const name = formatName(profile.first_name, profile.last_name, profile.email);
      const grade = student?.education_level ? `Grade: ${student.education_level}` : "Student";
      const subjects = student?.subjects_of_interest?.length
        ? `Subjects: ${student.subjects_of_interest.join(", ")}`
        : "";
      const description = [grade, subjects, profile.email].filter(Boolean).join(" • ");

      return {
        id: profile.id,
        role: "student" as const,
        firstName: profile.first_name || "",
        lastName: profile.last_name || "",
        email: profile.email,
        timezone: profile.timezone,
        profilePictureUrl: profile.profile_picture_url,
        description,
        keywords: joinKeywords([
          name,
          profile.email,
          student?.education_level,
          student?.subjects_of_interest,
          student?.location,
          profile.timezone,
        ]),
      };
    })
  );
};

const fetchTutorsForScheduling = async (): Promise<SchedulingParticipant[]> => {
  const { data: profilesData, error: profilesError } = await supabase
    .from("profiles")
    .select("id, first_name, last_name, email, timezone, profile_picture_url, created_at")
    .eq("user_type", "tutor")
    .order("created_at", { ascending: false });

  if (profilesError) throw profilesError;
  if (!profilesData?.length) return [];

  const tutorIds = profilesData.map((profile) => profile.id);
  const { data: tutorsData, error: tutorsError } = await supabase
    .from("tutors")
    .select("id, subjects_taught, expertise, total_years_exp, verification_status, is_live")
    .in("id", tutorIds);

  if (tutorsError) throw tutorsError;

  const tutorsById = new Map((tutorsData || []).map((tutor) => [tutor.id, tutor]));

  return markRecent(
    profilesData.map((profile) => {
      const tutor = tutorsById.get(profile.id);
      const name = formatName(profile.first_name, profile.last_name, profile.email);
      const subjects = tutor?.subjects_taught?.length
        ? tutor.subjects_taught.join(", ")
        : "Tutor";
      const experience =
        typeof tutor?.total_years_exp === "number"
          ? `${tutor.total_years_exp}+ years experience`
          : "";
      const status = tutor?.verification_status ? `Status: ${tutor.verification_status}` : "";
      const description = [subjects, experience, status].filter(Boolean).join(" • ");

      return {
        id: profile.id,
        role: "tutor" as const,
        firstName: profile.first_name || "",
        lastName: profile.last_name || "",
        email: profile.email,
        timezone: profile.timezone,
        profilePictureUrl: profile.profile_picture_url,
        description,
        keywords: joinKeywords([
          name,
          profile.email,
          tutor?.subjects_taught,
          tutor?.expertise,
          tutor?.total_years_exp,
          tutor?.verification_status,
          profile.timezone,
        ]),
      };
    })
  );
};

export const fetchSchedulingParticipants = async (): Promise<SchedulingParticipantsResponse> => {
  const [students, tutors] = await Promise.all([
    fetchStudentsForScheduling(),
    fetchTutorsForScheduling(),
  ]);

  return { students, tutors };
};

export const fetchSchedulingPurchasedPlans = async (): Promise<SchedulingPurchasedPlan[]> => {
  const { data, error } = await supabase
    .from("batches")
    .select(
      `
        id,
        name,
        student_id,
        product_id,
        product_name,
        product_type,
        default_tutor_id,
        status,
        start_date,
        end_date,
        total_sessions,
        remaining_sessions,
        subscription_id,
        student:profiles!batches_student_id_fkey(first_name, last_name, email),
        tutor:profiles!batches_default_tutor_id_fkey(first_name, last_name, email)
      `
    )
    .not("subscription_id", "is", null)
    .eq("status", "active")
    .order("created_at", { ascending: false });

  if (error) throw error;

  const subscriptionIds = Array.from(
    new Set((data || []).map((batch: { subscription_id?: string | null }) => batch.subscription_id).filter(Boolean))
  ) as string[];
  const { data: subscriptions, error: subscriptionsError } = subscriptionIds.length
    ? await supabase.from("subscriptions").select("id, product_id").in("id", subscriptionIds)
    : { data: [], error: null };

  if (subscriptionsError) throw subscriptionsError;

  const productIdBySubscriptionId = new Map(
    (subscriptions || []).map((subscription) => [subscription.id, subscription.product_id])
  );
  const productIdByBatchId = new Map(
    (data || []).map((batch: { id: string; product_id?: string | null; subscription_id?: string | null }) => [
      batch.id,
      batch.product_id || (batch.subscription_id ? productIdBySubscriptionId.get(batch.subscription_id) : undefined),
    ])
  );
  const productIds = Array.from(new Set([...productIdByBatchId.values()].filter(Boolean))) as string[];
  const { data: products, error: productsError } = productIds.length
    ? await supabase.from("products").select("id, subject_id").in("id", productIds)
    : { data: [], error: null };

  if (productsError) throw productsError;

  const subjectIds = Array.from(
    new Set((products || []).map((product: { subject_id?: string | null }) => product.subject_id).filter(Boolean))
  ) as string[];
  const { data: subjects, error: subjectsError } = subjectIds.length
    ? await supabase.from("subjects").select("id, name").in("id", subjectIds)
    : { data: [], error: null };

  if (subjectsError) throw subjectsError;

  const productsById = new Map((products || []).map((product) => [product.id, product]));
  const subjectsById = new Map((subjects || []).map((subject) => [subject.id, subject.name]));

  return (data || []).map((batch: any) => {
    const product = productsById.get(productIdByBatchId.get(batch.id));
    const totalSessions = batch.total_sessions;
    // `batches.remaining_sessions` is the authoritative, mutable balance for scheduling.
    const remainingSessions = batch.remaining_sessions;

    return {
      id: batch.id,
      name: batch.name,
      student_id: batch.student_id,
      product_name: batch.product_name,
      product_type: batch.product_type,
      subjectName: product?.subject_id ? subjectsById.get(product.subject_id) : undefined,
      default_tutor_id: batch.default_tutor_id,
      status: batch.status,
      start_date: batch.start_date,
      end_date: batch.end_date,
      total_sessions: totalSessions,
      remaining_sessions: remainingSessions,
      subscription_id: batch.subscription_id,
      studentName: formatName(batch.student?.first_name, batch.student?.last_name, batch.student?.email),
      tutorName: batch.tutor
        ? formatName(batch.tutor.first_name, batch.tutor.last_name, batch.tutor.email)
        : undefined,
    };
  });
};

export const fetchBatchCurriculumUnitsForScheduling = async (
  batchId: string
): Promise<SchedulingBatchCurriculumUnit[]> => {
  const { data: batchUnits, error: batchUnitsError } = await supabase
    .from("batch_curriculum_units")
    .select("id, unit_id, custom_tutor_id")
    .eq("batch_id", batchId)
    .eq("status", "active");

  if (batchUnitsError) throw batchUnitsError;
  if (!batchUnits?.length) return [];

  const unitIds = batchUnits.map((batchUnit) => batchUnit.unit_id);
  const { data: units, error: unitsError } = await supabase
    .from("curriculum_units")
    .select("id, name, curriculum_domains!curriculum_units_domain_id_fkey(name)")
    .in("id", unitIds);

  if (unitsError) throw unitsError;

  return batchUnits.map((batchUnit: any) => {
    const unit = (units || []).find((item: any) => item.id === batchUnit.unit_id);
    const domain = Array.isArray(unit?.curriculum_domains)
      ? unit.curriculum_domains[0]
      : unit?.curriculum_domains;

    return {
      id: batchUnit.id,
      unit_id: batchUnit.unit_id,
      name: unit?.name || "Unknown unit",
      custom_tutor_id: batchUnit.custom_tutor_id,
      subjectName: domain?.name,
    };
  });
};

const getPurchasedPlanOrThrow = async (batchId: string): Promise<SchedulingPurchasedPlan> => {
  const plans = await fetchSchedulingPurchasedPlans();
  const plan = plans.find((item) => item.id === batchId);

  if (!plan) {
    throw new Error("Selected purchased plan is no longer active or available.");
  }

  return plan;
};

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const parseLocalDate = (date: string) => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatLocalDate = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const generateWeeklyOccurrences = ({
  startDate,
  startTime,
  weekdays,
  capacity,
  timezone,
}: {
  startDate: string;
  startTime: string;
  weekdays: number[];
  capacity: number;
  timezone: string;
}) => {
  const selectedWeekdays = Array.from(new Set(weekdays)).sort((a, b) => a - b);
  if (!selectedWeekdays.length || selectedWeekdays.some((weekday) => weekday < 0 || weekday > 6)) {
    throw new Error("Choose one or more valid weekdays for the recurring sessions.");
  }

  const occurrences: string[] = [];
  let cursor = parseLocalDate(startDate);

  while (occurrences.length < capacity) {
    const occurrenceDate = formatLocalDate(cursor);
    if (selectedWeekdays.includes(cursor.getDay())) {
      occurrences.push(fromZonedTime(`${occurrenceDate}T${startTime}:00`, timezone).toISOString());
    }
    cursor = addDays(cursor, 1);
  }

  return occurrences;
};

export const createAdminOnDemandSession = async (
  input: SessionWriteInput
): Promise<SessionData> => {
  const plan = input.batch_id ? await getPurchasedPlanOrThrow(input.batch_id) : null;
  const remainingSessions = plan?.remaining_sessions ?? 0;

  if (plan && remainingSessions <= 0) {
    throw new Error("This purchased plan has no remaining sessions.");
  }

  const session = await sessionsService.createOnDemandSession({
    ...input,
    created_by: "admin",
    session_details: input.session_details,
    create_teams_meeting: true,
  });

  return session;
};

export const createAdminRecurringSessions = async (
  input: CreateAdminRecurringSessionsInput
): Promise<SessionData[]> => {
  const {
    weekdays,
    start_date,
    timezone,
    ...sessionInput
  } = input;
  const plan = await getPurchasedPlanOrThrow(input.batch_id);
  const remainingSessions = plan.remaining_sessions ?? 0;

  if (remainingSessions <= 0) {
    throw new Error("This purchased plan has no remaining sessions.");
  }

  const occurrences = generateWeeklyOccurrences({
    startDate: start_date,
    startTime: formatInTimeZone(sessionInput.scheduled_at, timezone, "HH:mm"),
    weekdays,
    capacity: remainingSessions,
    timezone,
  });

  if (!occurrences.length) {
    throw new Error("No recurring sessions could be generated from the selected weekdays.");
  }

  const sessions = await sessionsService.createRecurringSessions({
    ...sessionInput,
    student_id: plan.student_id,
    tutor_id: sessionInput.tutor_id || plan.default_tutor_id,
    recurring_pattern: {
      frequency: "weekly",
      interval: 1,
      days_of_week: Array.from(new Set(weekdays)).sort((a, b) => a - b),
      count: occurrences.length,
      timezone,
      subscription_id: plan.subscription_id,
      batch_id: plan.id,
    },
    occurrences,
    created_by: "admin",
    create_teams_meeting: true,
  });

  return sessions;
};
